import { FormattedMessage } from 'react-intl';

import type {
  Company,
  CompanyRecommendation,
  Maybe,
} from '@xing-com/crate-common-graphql-types';
import { EntityPageSocialProofType } from '@xing-com/crate-common-graphql-types';
import type { XingId } from '@xing-com/hub';

import { CompanyCard } from '../../components/company-card';
import {
  trackDismissRecommendation,
  useNwtTrackingCompanies,
} from '../../utils/tracking';

import * as Styled from './recommended-companies.styles';

export const getRecommendedMessageReasonId = (
  proofType: EntityPageSocialProofType,
  total: number
) => {
  const hasSingleUser = total === 1;

  if (proofType === EntityPageSocialProofType.Employees) {
    if (hasSingleUser) {
      return 'EP_COMPANY_SOCIAL_PROOF.one';
    }
    if (!hasSingleUser) {
      return 'EP_COMPANY_SOCIAL_PROOF.other';
    }
  }

  if (proofType === EntityPageSocialProofType.Followers) {
    if (hasSingleUser) {
      return 'CP_200_ONE_FOLLOWER_THIS_COMPANY';
    }
    if (!hasSingleUser) {
      return 'CP_200_FOLLOWERS_THIS_COMPANY';
    }
  }

  return 'CP_200_FOLLOWERS_THIS_COMPANY';
};
export const getRecommendedMessageReasonVariables = (
  proofType: EntityPageSocialProofType,
  users: Maybe<XingId>[]
) => {
  if (proofType === EntityPageSocialProofType.Employees) {
    if (users?.length === 1) {
      const [user] = users;
      return {
        username: user?.firstName,
      };
    }
    if (users?.length > 1) {
      return {
        numberOfContacts: users?.length,
      };
    }
  }

  if (proofType === EntityPageSocialProofType.Followers) {
    if (users?.length === 1) {
      const [user] = users;
      return {
        name: user?.firstName,
      };
    }
  }

  return {};
};

export type RecommendedCompanyCardProps = {
  recommendedCompany: CompanyRecommendation;
  onRemoveButtonClick: (companyRecommendation: Maybe<Company>) => void;
  position: number;
  onClick: () => void;
};
export const RecommendedCompanyCard: React.FC<RecommendedCompanyCardProps> = ({
  recommendedCompany,
  onRemoveButtonClick,
  position,
  onClick,
}) => {
  const trackNwtEvent = useNwtTrackingCompanies();

  const company = recommendedCompany?.company;
  const isFollowing = company?.userContext?.followState?.isFollowing || false;

  return (
    <CompanyCard
      key={company?.id}
      company={company as $TSFixMe}
      buttons={[
        recommendedCompany?.dismissible ? (
          <Styled.RemoveButton
            key="removeButton"
            size="small"
            onClick={() => {
              trackDismissRecommendation(company?.id);
              if (onRemoveButtonClick) onRemoveButtonClick(company as $TSFixMe);
            }}
          >
            <FormattedMessage id="CP_NEWSFEED_REMOVE" />
          </Styled.RemoveButton>
        ) : null,
        <Styled.FollowButton
          key="followButton"
          disabled={company?.entityPageId ? false : true}
          entityPageId={company?.entityPageId || ''}
          isFollowing={isFollowing}
          companyId={company?.id as string}
          followText="CP_200_FOLLOW_LINK"
          position={position}
        />,
      ]}
      onClick={onClick}
      onWasSeen={() => {
        trackNwtEvent({
          eventSchema: 'basic',
          page: 'companies/overview',
          event: 'entered_viewport',
          element: 'company_card',
          position,
          itemUrn: `surn:x-xing:companies:company:${company?.id}`,
        });
      }}
    />
  );
};

export default RecommendedCompanyCard;
